import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../../api";
import { uploadDmsPdf, buildDmsPdfFromApi, refreshStoredDmsPdf } from "../utils/dmsToPdf";
import { buildDmsReportDoc, dmsReportFileName } from "../utils/dmsReportToPdf";
import downloadDmsAttachment from "../utils/downloadDmsAttachment";
import EcoNotesPanel from "./EcoNotesPanel";
import { FaDownload } from "react-icons/fa";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Nav,
    Input,
    Label,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    Row,
    Col,
    Table,
    Spinner,
    Button,
    FormGroup,
    Alert,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem
} from "reactstrap";
import { EmptyState } from "../../../Components/Common/DashboardKit";
import { usePermissions } from "../../../hooks/usePermissions";
import "../../../assets/styles/ecnModal.css";
// Round R8 (2026) — ECN Tasks subgrid (Gap 9). Tab is added only for ECN
// doctype; see the tabs definition below.
import EcnTasksTab from "./tabs/EcnTasksTab";
// Round DMS-UAT-1 (2026) — shared ECN Will Change checklist (adds the new
// "ECR/CR Change Request" option, Issue 9) + Product Info line parsing
// (Issue 7). Both operate on the existing storage format — no schema change.
import { normalizeWillChangeValues } from "../constants/ecnWillChangeOptions";
import EcnWillChangeGrid from "./EcnWillChangeGrid";
import { parseProductInfoRows, PRODUCT_INFO_FIELDS } from "../utils/productInfoRows";
import { formatRev } from "../utils/dmsRev";
import useBackCloses from "../../../hooks/useBackCloses";
import { useTrailLeaf } from "../../../Components/Common/DashboardKit/PageHeaderTrailContext";

// ───────────────────────────────────────────────────────────────────────────
// Round R4 (2026) — Manager action bar configuration.
//
// The legacy DMSNew ribbon (DMSNew.vb:441–462) enabled per-action buttons
// based on the document's Status. Translating that matrix into modern:
//
//   Status 0  (Pending Approval)        → Approve / Deny (self-approvers),
//                                          Take-over (approvers or admin)
//   Status 1  (Approved-Awaiting Rel)   → Release, Obsolete, Void
//   Status 2  (Approved & Released)     → Un-Release, Obsolete, Void
//   Status 50 (Rejected / Needs Rev)    → Void  (author edits via normal flow)
//   Status 51 (Obsolete)                → De-Obsolete, Void
//   Status 54 (Void)                    → none (terminal)
//   Status 55 (Awaiting Rel, tasks open)→ Release, Obsolete, Void
//   Status 56 (Awaiting Rel, complete)  → Release, Obsolete, Void
//   Status 53 (Abandoned, legacy copy)  → Void  (Round DMS-Legacy-Parity-1b)
//   Status 60 / 99 (Draft / Saved)      → Void  (release manager; editing via Save path)
//
// Each entry below carries:
//   key         → matches the POST path segment (/api/dms/{id}/<key>)
//   label       → button caption
//   icon        → Remix icon class
//   tone        → reactstrap color (success/warning/danger/primary)
//   confirmTitle/Message/Label  → strings rendered in the per-action modal,
//                                  matching legacy DMSNew wording verbatim
//                                  where it exists
//   needsImplDate → /release-style date picker in the confirm modal
// ───────────────────────────────────────────────────────────────────────────
const ACTION_DEFS = {
    approve: {
        key: "approve", label: "Approve", icon: "ri-check-line", tone: "success",
        confirmTitle: "Confirm Approval",
        confirmMessage: "Please confirm your approval for this document. This will record your approval against the document's approval list.",
        confirmLabel: "Approve",
    },
    deny: {
        // Legacy DMSNew.vb:2307 used "Needs Revision" wording. The 5-char min
        // comments enforcement comes in Round R5. Round R4 sends the deny
        // request with the current ApprovalDto shape — comments are deferred.
        key: "deny", label: "Needs Revision", icon: "ri-arrow-go-back-line", tone: "warning",
        confirmTitle: "Request Revision",
        confirmMessage: "Mark this document as Needs Revision. The author will be notified and the document returns to the editing state.",
        confirmLabel: "Send for Revision",
    },
    "take-over-edit": {
        key: "take-over-edit", label: "Take over and edit", icon: "ri-edit-line", tone: "primary",
        confirmTitle: "Please Confirm Author Change",
        // Verbatim from legacy DMSNew.vb:3378 per Q19 decision.
        confirmMessage:
            "By editing a document within the approval process, you will become the author of the document. This operation cannot be undone. If you did not intend to do this, click 'Needs Revision' instead and provide your suggestions to the original author. Click 'Yes' to become the new author, or click 'No' to cancel.",
        confirmLabel: "Yes, take over",
    },
    "start-revision": {
        key: "start-revision", label: "Start New Revision", icon: "ri-file-copy-2-line", tone: "primary",
        confirmTitle: "Start New Revision",
        // Round DMS-2 (owner request #6): released docs were a dead end —
        // this clones the doc into an editable rev+1 DRAFT that routes
        // through the normal submit-for-approval → release pipeline.
        confirmMessage:
            "Start a new revision of this document? A fresh editable draft is created with YOU as its author (legacy rule); the current revision stays in force until the new one is approved AND released. Approvers and attachments are chosen fresh on the new revision.",
        confirmLabel: "Start Revision",
    },
    release: {
        key: "release", label: "Release", icon: "ri-send-plane-line", tone: "success",
        confirmTitle: "Release Document",
        confirmMessage: "Please confirm that you wish to release this document.",
        confirmLabel: "Release",
        needsImplDate: true,
    },
    unrelease: {
        key: "unrelease", label: "Un-Release", icon: "ri-arrow-go-back-line", tone: "warning",
        confirmTitle: "Un-Release Document",
        confirmMessage: "Please confirm that you wish to UN-release this document. It will return to the Awaiting Release queue.",
        confirmLabel: "Un-Release",
    },
    obsolete: {
        key: "obsolete", label: "Obsolete", icon: "ri-archive-line", tone: "danger",
        confirmTitle: "Mark Obsolete",
        confirmMessage: "Are you sure you wish to mark this document as obsolete? It will be moved to the Archived list.",
        confirmLabel: "Obsolete",
    },
    "de-obsolete": {
        key: "de-obsolete", label: "Mark as Released", icon: "ri-refresh-line", tone: "info",
        confirmTitle: "De-Obsolete Document",
        confirmMessage: "Return this document to the Released state. The implementation date will be re-stamped to today.",
        confirmLabel: "Mark as Released",
    },
    void: {
        key: "void", label: "Void", icon: "ri-delete-bin-line", tone: "danger",
        confirmTitle: "Void Document",
        confirmMessage:
            "Are you sure you wish to void this document? It will no longer be applicable within this system. This action is recorded in the revision log and the document will not appear in default document listings.",
        confirmLabel: "Void Document",
    },
};

// Per-status visibility matrix. Returns array of action keys to show.
function visibleActionKeysForStatus(status, ctx) {
    const out = [];
    const { canApprove, isApproverOnDoc, isReleaseManager } = ctx;

    if (status === 0) {
        if (canApprove)                                 { out.push("approve", "deny"); }
        if (isApproverOnDoc || isReleaseManager)        { out.push("take-over-edit"); }
    } else if (status === 1 || status === 55 || status === 56) {
        // Round DMS-5 audit: backend start-revision now accepts the whole
        // approved family (1/2/55/56) — surface it here too.
        // Round DMS-Legacy-Parity-5: legacy "Edit Document" (rev-up) was
        // available to every user on a released document (the reviser becomes
        // the author); owner: "start new revision for all". Backend enforces
        // the same rule (dms.create).
        if (ctx.canStartRevision) out.push("start-revision");
        if (isReleaseManager) out.push("release", "obsolete", "void");
    } else if (status === 2) {
        // Round DMS-2 (owner request #6): the author, the document manager,
        // or Document Control can begin a rev+1 draft from a released doc
        // (the server re-checks the same relationships/roles).
        // Round DMS-Legacy-Parity-5: legacy "Edit Document" (rev-up) was
        // available to every user on a released document (the reviser becomes
        // the author); owner: "start new revision for all". Backend enforces
        // the same rule (dms.create).
        if (ctx.canStartRevision) out.push("start-revision");
        if (isReleaseManager) out.push("unrelease", "obsolete", "void");
    } else if (status === 50) {
        if (isReleaseManager) out.push("void");
    } else if (status === 51) {
        if (isReleaseManager) out.push("de-obsolete", "void");
    } else if (status === 53 || status === 60 || status === 99) {
        // Round DMS-Legacy-Parity-1b: legacy allowed Void from any state.
        // Abandoned copies (53 — legacy leftovers Ivette voids) and saved /
        // draft documents (99 / 60, e.g. left by an absent engineer) can be
        // voided by the release manager, exactly as in the legacy window.
        if (isReleaseManager) out.push("void");
    }
    // status 54 (void) → no actions (terminal)

    return out;
}

// Map numeric DocType → URL slug for /dms/edit/<docType>/<dmsid>.
// Mirrors CreateEcn.js handling.
const DOCTYPE_SLUG = { 1: "sop", 2: "wi", 3: "fcd", 4: "ecn", 5: "lit" };

// Helper — today as yyyy-mm-dd (matches AwaitingRelease.js)
function todayIso() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function DmsViewModal({ isOpen, toggle, dmsId }) {
    // Owner 2026-10-12: the browser's Back button closes the document instead of
    // leaving the list it was opened from.
    useBackCloses(isOpen, toggle);
    // Round DMS-IssueRound-1 (2026) — Issue 1/6: the Round DMS-UAT-23d swap that
    // rewrote a DRAFT-<dmsid> placeholder into "ECN-<dmsid>" is gone — that
    // was the row id dressed up as a document number. Drafts now own their
    // real number from the first save, so the stored DocID is shown as-is.
    const displayDocId = (docId) => (docId || "");
    // Round DMS-UAT-1 (2026) — Issue 9: the checklist now comes from the
    // shared constants module (adds "ECR/CR Change Request", fixes the
    // "Use to Deplation" typo with a load-time alias for old data).

    const displayDmsLabel = (d) => `${displayDocId(d?.header?.docID, d?.header?.docType) || ""} Rev ${d?.header?.rev ?? ""}`.trim();
    const [loading, setLoading] = useState(false);
    // Owner (2026-10-02): "there is no print preview, only print / report". The
    // document and the four reports open in a preview window first (the browser's
    // PDF viewer), with Print and Download there.
    const [preview, setPreview] = useState(null);          // { url, title, fileName }
    const [previewBusy, setPreviewBusy] = useState(false);
    const previewFrameRef = useRef(null);
    const showPreview = (blob, title, fileName) => setPreview((prev) => {
        if (prev?.url) window.URL.revokeObjectURL(prev.url);
        return { url: window.URL.createObjectURL(blob), title, fileName };
    });
    const closePreview = () => setPreview((prev) => {
        if (prev?.url) window.URL.revokeObjectURL(prev.url);
        return null;
    });
    useEffect(() => () => { if (preview?.url) window.URL.revokeObjectURL(preview.url); }, [preview]);
    const printFromPreview = () => {
        try {
            const w = previewFrameRef.current?.contentWindow;
            if (!w) throw new Error("no frame");
            w.focus();
            w.print();
        } catch {
            // Some browsers do not print a PDF inside a frame: open it in a tab instead.
            if (preview?.url) window.open(preview.url, "_blank", "noopener");
        }
    };
    const downloadFromPreview = () => {
        if (!preview?.url) return;
        const a = window.document.createElement("a");
        a.href = preview.url;
        a.download = preview.fileName || "document.pdf";
        window.document.body.appendChild(a);
        a.click();
        window.document.body.removeChild(a);
    };
    const [document, setDocument] = useState(null);
    const [activeTab, setActiveTab] = useState("1");

    // Phase 2.6 — extra data sources surfaced as new tabs.
    //   revisions     → /api/dms/{id}/revisions (Phase 2.3)
    //   customerApproval → /api/dms/{id}/customer-approval (Phase 2.4, ECN only)
    // Each is fetched in parallel with /full but the modal stays usable
    // even if these calls fail (we just hide the tab content).
    const [revisions, setRevisions] = useState([]);
    const [revisionsError, setRevisionsError] = useState(null);
    // Round DMS-4 — main-fetch failure message (e.g. archived-doc gate).
    const [loadError, setLoadError] = useState(null);
    const [customerApproval, setCustomerApproval] = useState(null);
    const [customerApprovalError, setCustomerApprovalError] = useState(null);

    // Phase 2.7 — Customer Approval inline editor state
    const [caEditMode, setCaEditMode] = useState(false);
    const [caForm, setCaForm] = useState({
        custApprovalRequired: false,
        custNotificationRequired: false,
        custNone: false,
        approvalObtained: false,
        notificationProvided: false,
        // Round R9 (2026) — Q17 — free-text notes captured at resolution
        // time. Persisted server-side to DmsEcr.customerApprovalNotes /
        // customerNotificationNotes (existing columns, no schema change).
        customerApprovalNotes: "",
        customerNotificationNotes: "",
    });
    const [caSaving, setCaSaving] = useState(false);
    const [caSaveError, setCaSaveError] = useState(null);

    // ───────────────────────────────────────────────────────────────────
    // Round R4 (2026) — Manager action bar state.
    //
    // pendingAction holds the currently-confirming action (one of the
    // ACTION_DEFS keys) or null when no confirm modal is open.
    // implDate / denyComments are populated only when the relevant
    // action needs them; both are reset every time the user opens
    // (or cancels) the modal.
    //
    // Round R5 will add full deny-comments enforcement (5-char min,
    // server-side validation). Round R4 sends deny without comments —
    // matches the current /api/dms/{id}/deny contract.
    // ───────────────────────────────────────────────────────────────────
    const [pendingAction, setPendingAction] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [actionError, setActionError] = useState(null);
    const [implDate, setImplDate] = useState(todayIso());
    // Round R5 (2026) — Denial comments. Required when pendingAction.key
    // === "deny" (5-char minimum, 255-char max, both enforced server-side).
    // Reset every time the confirm modal opens.
    const [denyComments, setDenyComments] = useState("");

    const navigate = useNavigate();
    const { canAccessModule, isItAdmin } = usePermissions();
    // Round DMS-Legacy-Parity-8: IT Admin is the super user — every DMS
    // action at every status, whatever the module bundle says (the backend
    // policies and CallerIsDocumentControl already treat isItAdmin that way).
    const isReleaseManager = canAccessModule("dms.release") || Boolean(isItAdmin);

    // Derive caller's empId from the session — same pattern as
    // AwaitingRelease.js / MyPendingApprovals.js.
    const empId = useMemo(() => {
        try { return Number(JSON.parse(sessionStorage.getItem("authUser") || "{}").uid); }
        catch { return 0; }
    }, []);

    // Round DMS-UAT-1 (2026) — "Document Control" for the purposes of
    // edit-at-any-status and Properties editing.
    // Round DMS-UAT-3 — legacy parity fix: the WinForms release window
    // (with Edit Document) opens for the RELEASE-permission holder, so
    // Document Control here = dms.release module (the "Documents Ready for
    // Release" permission) OR the isDmsManager/isItAdmin claims. This now
    // matches the backend's CallerIsDocumentControl() exactly, so the Edit
    // button only appears when the API would accept the action. The server
    // remains the authority either way.
    const claimDocControl = useMemo(() => {
        try {
            const u = JSON.parse(sessionStorage.getItem("authUser") || "{}");
            return Boolean(u.isItAdmin || u.isDmsManager);
        } catch { return false; }
    }, []);
    // Round DMS-UAT-3 — Document Control = admin claims OR the dms.release
    // module (the "Documents Ready for Release" permission), matching the
    // backend CallerIsDocumentControl() and the legacy release window.
    const isDocControl = claimDocControl || isReleaseManager;

    const openCustomerApprovalEditor = () => {
        // Seed the form from current state (if any). If null, all start false.
        setCaForm({
            custApprovalRequired: customerApproval?.custApprovalRequired ?? false,
            custNotificationRequired: customerApproval?.custNotificationRequired ?? false,
            custNone: customerApproval?.custNone ?? false,
            approvalObtained: customerApproval?.approvalObtained ?? false,
            notificationProvided: customerApproval?.notificationProvided ?? false,
            // Round R9 (2026) — Q17 — pre-fill from whichever payload
            // carried the notes (the GET /customer-approval endpoint
            // surfaces them; the /full endpoint also exposes them in
            // ecnDetails — either source is fine).
            customerApprovalNotes: customerApproval?.customerApprovalNotes
                ?? document?.ecnDetails?.[0]?.customerApprovalNotes ?? "",
            customerNotificationNotes: customerApproval?.customerNotificationNotes
                ?? document?.ecnDetails?.[0]?.customerNotificationNotes ?? "",
        });
        setCaSaveError(null);
        setCaEditMode(true);
    };
    const cancelCustomerApprovalEdit = () => {
        if (caSaving) return;
        setCaEditMode(false);
        setCaSaveError(null);
    };
    const saveCustomerApproval = async () => {
        setCaSaving(true);
        setCaSaveError(null);
        try {
            const empId = Number(JSON.parse(sessionStorage.getItem("authUser") || "{}").uid);
            const res = await api.put(`/dms/${dmsId}/customer-approval`, {
                updatedBy: empId,
                ...caForm,
            });
            setCustomerApproval(res.data);
            setCaEditMode(false);
        } catch (err) {
            setCaSaveError(err?.response?.data?.error || err?.message || "Failed to save.");
        } finally {
            setCaSaving(false);
        }
    };

    const toggleTab = (tab) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    // ───────────────────────────────────────────────────────────────────
    // Round DMS-UAT-1 (2026) — Issue 15/16: Properties tab edit mode.
    //
    // Authorized users (document author while the doc is mutable, or
    // Document Control at any status for the notification list; approver
    // changes are only possible at Status 0/60 — the backend enforces
    // both rules and audits every change) can add/remove approvers and
    // electronic-notification recipients using the existing endpoints:
    //   POST/DELETE /api/dms/{id}/approvers
    //   POST/DELETE /api/dms/{id}/notifications
    // ───────────────────────────────────────────────────────────────────
    const [propsEditMode, setPropsEditMode] = useState(false);
    const [propsBusy, setPropsBusy] = useState(false);
    const [propsError, setPropsError] = useState(null);
    const [newApproverId, setNewApproverId] = useState("");
    const [newNotifyId, setNewNotifyId] = useState("");

    const parseEmpIdInput = (raw) => {
        const n = Number(String(raw).trim());
        return Number.isFinite(n) && n > 0 ? n : null;
    };

    const propsAction = async (fn, okMsg) => {
        setPropsBusy(true);
        setPropsError(null);
        try {
            await fn();
            if (okMsg) toast.success(okMsg);
            // re-pull /full so approvals/notifications (with names) refresh
            const res = await api.get(`/dms/${dmsId}/full`);
            setDocument(res.data);
        } catch (err) {
            setPropsError(err?.response?.data?.error || err?.message || "Action failed.");
        } finally {
            setPropsBusy(false);
        }
    };

    const addApprover = () => {
        const idNum = parseEmpIdInput(newApproverId);
        if (!idNum) { setPropsError("Enter a valid numeric EmpID for the approver."); return; }
        propsAction(
            () => api.post(`/dms/${dmsId}/approvers`, { approverId: idNum }),
            `Approver ${idNum} added.`
        ).then(() => setNewApproverId(""));
    };
    const removeApprover = (idNum) => {
        propsAction(
            async () => {
                const res = await api.delete(`/dms/${dmsId}/approvers/${idNum}`);
                // Go-live parity (2026-09-24): removing the last pending approver
                // may complete the approval (Status 1) — store a PDF that matches.
                if (res?.data?.awaitingRelease) {
                    toast.info("All remaining approvers had approved — the document is now awaiting release.");
                    refreshStoredDmsPdf(dmsId, api, document?.header?.logoType);
                }
            },
            `Approver ${idNum} removed.`
        );
    };
    const addNotification = () => {
        const idNum = parseEmpIdInput(newNotifyId);
        if (!idNum) { setPropsError("Enter a valid numeric EmpID for the notification list."); return; }
        propsAction(
            () => api.post(`/dms/${dmsId}/notifications`, { employeeId: idNum }),
            `Notification recipient ${idNum} added.`
        ).then(() => setNewNotifyId(""));
    };
    const removeNotification = (idNum) => {
        propsAction(
            () => api.delete(`/dms/${dmsId}/notifications/${idNum}`),
            `Notification recipient ${idNum} removed.`
        );
    };

    const decodeUnicodeBase64 = (base64Value) => {
        if (!base64Value) return "";

        try {
            const binaryString = atob(base64Value);
            const bytes = Uint8Array.from(binaryString, (c) => c.charCodeAt(0));
            return new TextDecoder("utf-16le").decode(bytes);
        } catch {
            return base64Value;
        }
    };

    useEffect(() => {
        if (!isOpen || !dmsId) return;

        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setRevisions([]);
            setRevisionsError(null);
            setCustomerApproval(null);
            setCustomerApprovalError(null);
            setCaEditMode(false);
            setCaSaveError(null);
            // Round DMS-UAT-1 — reset Properties edit state per document.
            setPropsEditMode(false);
            setPropsError(null);
            setLoadError(null);
            setNewApproverId("");
            setNewNotifyId("");

            try {
                // Main fetch — must succeed for the modal to render at all.
                const res = await api.get(`/dms/${dmsId}/full`);
                const doc = res.data;

                if (!cancelled) {
                    setDocument(doc);
                    setActiveTab("1");
                }

                // Phase 2.6 — fire-and-forget side fetches (don't await).
                // If either fails, the corresponding tab just shows an
                // error message rather than blocking the whole modal.
                api.get(`/dms/${dmsId}/revisions`)
                    .then((r) => { if (!cancelled) setRevisions(r.data || []); })
                    .catch((e) => {
                        if (!cancelled) setRevisionsError(e?.response?.data?.error || "Failed to load revisions.");
                    });

                api.get(`/dms/${dmsId}/customer-approval`)
                    .then((r) => { if (!cancelled) setCustomerApproval(r.data); })
                    .catch((e) => {
                        // 404 here is normal — means no customer-approval row exists yet.
                        if (!cancelled) {
                            if (e?.response?.status === 404) {
                                setCustomerApproval(null);
                                setCustomerApprovalError(null);
                            } else {
                                setCustomerApprovalError(e?.response?.data?.error || "Failed to load customer approval.");
                            }
                        }
                    });
            } catch (err) {
                console.error("Failed to load document:", err);
                if (!cancelled) {
                    setDocument(null);
                    // Round DMS-4 — surface the server's message (notably
                    // the legacy archived-document notice from the new
                    // Status-51 gate) instead of a generic empty state.
                    setLoadError(err?.response?.data?.error || null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [isOpen, dmsId]);

    const docType = document?.header?.docType;
    const ecrFinalTitle = document?.ecnDetails?.[0]?.ecrTitle || "";
    console.log("Check Title of Ecr :", ecrFinalTitle);

    // ───────────────────────────────────────────────────────────────────
    // Round R4 (2026) — Manager action bar derivations.
    //
    // Re-runs whenever the document or session changes. None of these
    // values are read until the modal has actually loaded a document, so
    // null-safe access (?.) is enough.
    // ───────────────────────────────────────────────────────────────────
    const status = document?.header?.status;

    // The current user's row in the approval list, if any. Used to decide
    // whether they can Approve/Deny on this doc.
    const myApprovalRow = useMemo(
        () => (document?.approvals ?? []).find(
            (a) => Number(a.approver) === empId && (a.approved === null || a.approved === undefined)
        ),
        [document, empId]
    );

    // True if the caller has a pending DmsApproval row on this doc.
    const canApprove = status === 0 && !!myApprovalRow;

    // True if the caller appears on the doc's approval list at all
    // (regardless of pending state). Used by the take-over-edit gate.
    const isApproverOnDoc = useMemo(
        () => (document?.approvals ?? []).some((a) => Number(a.approver) === empId),
        [document, empId]
    );

    // Final list of action keys to render in the bar.
    // Round DMS-2 (owner request #6): the ctx now carries the caller's
    // relationship to THIS document (author / manager) and the Document
    // Control flag, so the Start-New-Revision action can gate correctly.
    const visibleActionKeys = useMemo(
        () => visibleActionKeysForStatus(status, {
            canApprove,
            isApproverOnDoc,
            isReleaseManager,
            isAuthorHere: Number(document?.header?.author) === empId,
            isManagerHere: Number(document?.header?.manager) === empId,
            isDocControl,
            // Round DMS-Legacy-Parity-5 (owner): anyone with DMS create access can
            // start a revision of any released / approved document, ECN included.
            canStartRevision: isDocControl || Boolean(isItAdmin) || canAccessModule("dms.create"),
        }),
        [status, canApprove, isApproverOnDoc, isReleaseManager, document, empId, isDocControl]
    );

    // The actual action handler. Dispatches to the right backend endpoint
    // based on pendingAction.key. All endpoints share the same response
    // shape (returns ok + updated status); on success the doc is re-fetched
    // so the action bar / status badge / tabs all reflect the new state.
    const openConfirm = (actionKey) => {
        setActionError(null);
        setImplDate(todayIso());
        setDenyComments("");              // Round R5 — fresh comments per open
        setPendingAction(ACTION_DEFS[actionKey]);
    };
    const closeConfirm = () => {
        if (actionLoading) return;
        setPendingAction(null);
        setActionError(null);
    };

    const refetchDocument = async () => {
        try {
            const res = await api.get(`/dms/${dmsId}/full`);
            setDocument(res.data);
        } catch (e) {
            // best-effort — keep the modal open even if refetch fails
            console.error("Refetch after action failed:", e);
        }
    };

    const executeAction = async () => {
        if (!pendingAction) return;
        setActionLoading(true);
        setActionError(null);
        try {
            let response;
            if (pendingAction.key === "approve") {
                response = await api.post(`/dms/${dmsId}/approve`, { approverId: empId });
            } else if (pendingAction.key === "deny") {
                // Round R5 (2026) — send the denial reason. The textarea
                // enforces 5+ chars before allowing submit; the backend
                // re-validates and stores the trimmed value in
                // DmsApproval.ApprovalString.
                response = await api.post(`/dms/${dmsId}/deny`, {
                    approverId: empId,
                    comments: denyComments,
                });
            } else if (pendingAction.key === "release") {
                response = await api.post(`/dms/${dmsId}/release`, {
                    releasedBy: empId,
                    implementationDate: implDate || null,
                });
            } else if (pendingAction.key === "start-revision") {
                response = await api.post(`/dms/${dmsId}/start-revision`);
                const newId = response?.data?.newDmsId;
                const slug = DOCTYPE_SLUG[document?.header?.docType] || "ecn";
                toast.success(response?.data?.message || "New revision draft created. Opening the editor…");
                setPendingAction(null);
                toggle();
                if (newId) navigate(`/dms/edit/${slug}/${newId}`);
                return;
            } else if (pendingAction.key === "take-over-edit") {
                response = await api.post(`/dms/${dmsId}/take-over-edit`);
                // After successful take-over, navigate straight to the editor.
                // The new author (the caller) and Status=50 are already
                // persisted server-side.
                const slug = DOCTYPE_SLUG[response?.data?.docType] || "ecn";
                toast.success("You are now the author. Opening the editor…");
                setPendingAction(null);
                toggle();  // close the view modal
                navigate(`/dms/edit/${slug}/${dmsId}`);
                return;
            } else {
                // unrelease / obsolete / de-obsolete / void — all body-less POSTs
                response = await api.post(`/dms/${dmsId}/${pendingAction.key}`);
            }

            // Success toast — use the message from the response if present.
            const message = response?.data?.message || `${pendingAction.label} succeeded.`;
            toast.success(message);
            setPendingAction(null);

            // Go-live parity (2026-09-24): the status changed — rebuild and store
            // the PDF so Print and the release mails match the new status
            // (fire-and-forget; the server dropped the stale one).
            refreshStoredDmsPdf(dmsId, api, document?.header?.logoType);

            // Refresh doc state so the action bar updates with the new status.
            await refetchDocument();
        } catch (err) {
            // Round DMS-UAT-1 (2026) — Issue 13: the release endpoint returns
            // 409 with the incomplete-task list. Inside this modal we surface
            // the list inline and point the user at the Awaiting Release page
            // (which carries the full override-with-reason flow) or at the
            // Tasks tab to complete them. Nothing was released; Cancel is safe.
            if (err?.response?.status === 409 && err?.response?.data?.incompleteTasks) {
                const list = err.response.data.incompleteTasks
                    .map((t) => `• ${t.description || `Task #${t.taskId}`}`)
                    .join("\n");
                setActionError(
                    `This document still has incomplete task(s):\n${list}\n\n` +
                    `Complete them in the Tasks tab, or release with an explicit ` +
                    `override from the Awaiting Release page.`
                );
            } else {
                const apiError = err?.response?.data?.error
                              || err?.response?.data?.message
                              || err?.message
                              || "Action failed.";
                setActionError(apiError);
            }
        } finally {
            setActionLoading(false);
        }
    };
    const tabs = useMemo(() => {

        if (!document) return [];

        const docType = document.header.docType;

        if (docType === 4) {
            return [
                // Round DMS-Legacy-Parity-3 (owner): Tasks first — the ECN action
                // items are what the release window is about. Tab ids are
                // unchanged (panes are keyed by id), only the strip order moved;
                // the document still opens on ECN Header.
                { id: "9", label: "Tasks" },
                { id: "1", label: "ECN Header" },
                { id: "2", label: "Product Info" },
                { id: "3", label: "ECN Details" },
                { id: "4", label: "Associated Docs" },
                { id: "5", label: "Attachments" },
                { id: "6", label: "Properties" },
                // Phase 2.6 — surfaces audit trail + customer approval state
                { id: "7", label: "Revisions" },
                { id: "8", label: "Customer Approval" }
            ];
        }

        const extraLabel = {
            1: "Procedure",
            2: "Work Instruction",
            3: "Form",
            5: "Notes"
        }[docType];

        return [
            { id: "1", label: "Document Info" },
            { id: "2", label: "Purpose" },
            { id: "3", label: "Scope" },
            { id: "4", label: "Responsibilities" },
            { id: "5", label: "Definitions" },
            { id: "6", label: extraLabel },
            { id: "7", label: "Associated Docs" },
            { id: "8", label: "Attachments" },
            { id: "9", label: "Properties" },
            // Phase 2.6 — non-ECN docs also get Revisions (no Customer Approval —
            // that's an ECN-specific concept).
            { id: "10", label: "Revisions" }
        ];

    }, [document, docType]);

    // ─── Phase 2.6 — Tab content renderers ────────────────────────────────
    // Reused for ECN (tabs 7 & 8) and non-ECN (tab 10 — Revisions only).
    // ----------------------------------------------------------------------
    const trainingBadge = (code) => {
        if (code === "Y") return <span className="badge bg-danger">Required</span>;
        if (code === "O") return <span className="badge bg-warning">Optional</span>;
        if (code === "N") return <span className="badge bg-secondary">No</span>;
        if (code === "X") return <span className="badge bg-light text-dark">N/A</span>;
        return <span className="text-muted">—</span>;
    };

    // ─────────────────────────────────────────────────────────────────
    // Round R10 (2026) — Revisions tab action-badge color mapping.
    //
    // The DmsRevLog.ChangeMade column carries free-text audit strings
    // written by WriteApprovalAuditAsync across Rounds 3-9:
    //
    //   • "APPROVED"                              → R3 base approval
    //   • "APPROVED (override by X on behalf …)"  → R3 admin override
    //   • "DENIED"                                → R3 base denial
    //   • "DENIED (override by X on behalf …)"    → R3 admin override
    //   • "RELEASED"                              → R3 release
    //   • "RELEASED (override by X …)"            → R3 admin override
    //   • "UN-RELEASED"                           → R4 lifecycle action
    //   • "OBSOLETED"                             → R4 lifecycle action
    //   • "DE-OBSOLETED"                          → R4 lifecycle action
    //   • "VOIDED (…)"                            → R4 lifecycle action
    //   • "AUTHOR-TAKEOVER by X (was Y)"          → R4 Q19 wording
    //   • "TASK ADDED (taskId=N): description"    → R8 task lifecycle
    //   • "TASK COMPLETED (taskId=N)"             → R8 task lifecycle
    //   • "TASK DELETED (taskId=N): description"  → R8 task lifecycle
    //
    // This helper bucketizes them so the badge color signals the
    // category at a glance without forcing the user to read the
    // entire ChangeMade string. Unknown strings fall back to "secondary".
    // ─────────────────────────────────────────────────────────────────
    const actionBadgeMeta = (changeMade) => {
        const text = (changeMade || "").toUpperCase();
        if (!text) return { color: "secondary", label: changeMade || "—" };
        // Order matters — more specific prefixes first.
        if (text.startsWith("APPROVED"))        return { color: "success", label: "APPROVED" };
        if (text.startsWith("RELEASED"))        return { color: "primary", label: "RELEASED" };
        if (text.startsWith("DE-OBSOLETED"))    return { color: "info",    label: "DE-OBSOLETED" };
        if (text.startsWith("OBSOLETED"))       return { color: "warning", label: "OBSOLETED" };
        if (text.startsWith("UN-RELEASED"))     return { color: "warning", label: "UN-RELEASED" };
        if (text.startsWith("VOIDED"))          return { color: "dark",    label: "VOIDED" };
        if (text.startsWith("DENIED"))          return { color: "danger",  label: "DENIED" };
        if (text.startsWith("AUTHOR-TAKEOVER")) return { color: "warning", label: "AUTHOR-TAKEOVER" };
        if (text.startsWith("TASK ADDED"))      return { color: "info",    label: "TASK ADDED" };
        if (text.startsWith("TASK COMPLETED"))  return { color: "success", label: "TASK COMPLETED" };
        if (text.startsWith("TASK DELETED"))    return { color: "danger",  label: "TASK DELETED" };
        return { color: "secondary", label: changeMade };
    };

    const renderRevisionsTab = (tabId) => (
        <TabPane tabId={tabId}>
            {revisionsError ? (
                <div className="alert alert-warning mb-0">
                    <i className="ri-error-warning-line me-2"></i>{revisionsError}
                </div>
            ) : revisions.length === 0 ? (
                <div className="text-muted text-center py-4">
                    <i className="ri-history-line" style={{ fontSize: 28 }}></i>
                    {/* Round DMS-UAT-1 — Issue 14: clear empty-state wording */}
                    <div className="mt-2">No revision history logged yet.</div>
                </div>
            ) : (
                <Table hover bordered responsive size="sm" className="align-middle mb-0">
                    <thead className="table-light">
                        {/* Round DMS-UAT-1 — Issue 14 label changes:
                            "When" → "Date"; the audit string previously shown
                            under "Action" now lives under "Change Made"; the
                            section/paragraph column is labeled
                            "Sec/Par. Changed" and sits before it. */}
                        <tr>
                            <th>Date</th>
                            <th>Rev</th>
                            <th>Sec/Par. Changed</th>
                            <th>Change Made</th>
                            <th>Changed By</th>
                            <th>Training</th>
                            <th>Trained</th>
                        </tr>
                    </thead>
                    <tbody>
                        {revisions.map((r) => {
                            // Round R10 — split into a colored category badge
                            // + the full original text shown next to it. The
                            // override-detail / task-detail tail of the string
                            // stays visible so nothing is lost from the audit
                            // trail — only the *categorization* is added.
                            // Owner finding (2026-10-01, FCD-17316): a logged revision ("Form revised
                            // to…", "Initial Release") was shown as a badge plus the same words with
                            // the first letter cut off ("orm revised…"). Logged revisions (they carry a
                            // Sec/Par.) are plain text; only audit rows get the category badge and the
                            // text after the known prefix.
                            const isAudit = r.secParaChanged == null;
                            const meta = isAudit ? actionBadgeMeta(r.changeMade) : null;
                            const detail = isAudit
                                ? (r.changeMade || "").slice((meta.label || "").length).replace(/^[\s\-–—:]+/, "").trim()
                                : "";
                            return (
                                <tr key={r.revLogId}>
                                    <td>{r.changeTimeStamp ? new Date(r.changeTimeStamp).toLocaleString() : "—"}</td>
                                    <td>{formatRev(r.version) || "—"}</td>
                                    <td>{r.secParaChanged || <span className="text-muted">—</span>}</td>
                                    <td>
                                        {isAudit ? (
                                            <>
                                                <span className={`badge bg-${meta.color}`} title={r.changeMade || ""}>
                                                    {meta.label}
                                                </span>
                                                {detail && detail !== meta.label && (
                                                    <div className="text-muted small mt-1" style={{ whiteSpace: "pre-wrap" }}>
                                                        {detail}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <span style={{ whiteSpace: "pre-wrap" }}>{r.changeMade || <span className="text-muted">—</span>}</span>
                                        )}
                                    </td>
                                    <td>
                                        {r.changedByName
                                            ? <>{r.changedByName} <span className="text-muted">({r.changedById})</span></>
                                            : (r.changedById ?? "—")}
                                    </td>
                                    <td>{trainingBadge(r.trainingRequired)}</td>
                                    <td>
                                        {r.trainingCompleted
                                            ? <span className="text-success">
                                                ✓ {r.trainingCompleteDate ? new Date(r.trainingCompleteDate).toLocaleDateString() : ""}
                                            </span>
                                            : <span className="text-muted">—</span>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            )}
        </TabPane>
    );

    const renderCustomerApprovalTab = (tabId) => (
        <TabPane tabId={tabId}>
            {customerApprovalError ? (
                <div className="alert alert-warning mb-0">
                    <i className="ri-error-warning-line me-2"></i>{customerApprovalError}
                </div>
            ) : caEditMode ? (
                renderCustomerApprovalEditor()
            ) : !customerApproval ? (
                <div className="text-muted text-center py-4">
                    <i className="ri-user-shared-line" style={{ fontSize: 28 }}></i>
                    <div className="mt-2">No customer-approval record for this document.</div>
                    <div className="small mt-1">Click "Edit" below to create one.</div>
                    <button
                        type="button"
                        className="btn btn-primary btn-sm mt-3"
                        onClick={openCustomerApprovalEditor}
                    >
                        <i className="ri-edit-line align-bottom me-1"></i>Edit
                    </button>
                </div>
            ) : (
                <>
                    <div className="d-flex justify-content-end mb-2">
                        <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={openCustomerApprovalEditor}
                        >
                            <i className="ri-edit-line align-bottom me-1"></i>Edit
                        </button>
                    </div>
                    <Row className="g-4">
                        <Col md={6}>
                            <h6 className="fw-semibold mb-3">Requirements</h6>
                            <Table size="sm" borderless className="mb-3">
                                <tbody>
                                    {/* Round DMS-UAT-1 — Issue 8: when "No customer
                                        involvement" was checked the old row read
                                        "No customer involvement: Yes", which UAT
                                        flagged as inverted. The row now answers the
                                        plain question "Customer involvement?" —
                                        "No" when custNone is checked, "Yes" when
                                        either requirement flag is set. */}
                                    <tr>
                                        <th className="text-muted" style={{ width: "55%" }}>Customer involvement</th>
                                        <td>
                                            {customerApproval.custNone
                                                ? <span className="badge bg-secondary">No — no customer involvement</span>
                                                : (customerApproval.custApprovalRequired || customerApproval.custNotificationRequired)
                                                    ? <span className="badge bg-info">Yes</span>
                                                    : <span className="text-muted">—</span>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th className="text-muted">Customer approval required</th>
                                        <td>{customerApproval.custApprovalRequired ? <span className="badge bg-info">Yes</span> : <span className="text-muted">No</span>}</td>
                                    </tr>
                                    <tr>
                                        <th className="text-muted">Customer notification required</th>
                                        <td>{customerApproval.custNotificationRequired ? <span className="badge bg-info">Yes</span> : <span className="text-muted">No</span>}</td>
                                    </tr>
                                </tbody>
                            </Table>
                        </Col>

                        <Col md={6}>
                            <h6 className="fw-semibold mb-3">Status</h6>
                            <Table size="sm" borderless className="mb-3">
                                <tbody>
                                    <tr>
                                        <th className="text-muted" style={{ width: "55%" }}>Approval obtained</th>
                                        <td>
                                            {customerApproval.approvalObtained
                                                ? <span className="text-success">✓ Yes</span>
                                                : <span className="text-muted">—</span>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th className="text-muted">Notification provided</th>
                                        <td>
                                            {customerApproval.notificationProvided
                                                ? <span className="text-success">✓ Yes</span>
                                                : <span className="text-muted">—</span>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th className="text-muted">Resolved by</th>
                                        {/* Round DMS-UAT-2 — name comes along with the ID */}
                                        <td>
                                            {customerApproval.resolvedBy != null
                                                ? (customerApproval.resolvedByName
                                                    ? <>{customerApproval.resolvedByName} <span className="text-muted">(ID: {customerApproval.resolvedBy})</span></>
                                                    : customerApproval.resolvedBy)
                                                : <span className="text-muted">—</span>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th className="text-muted">Resolved on</th>
                                        <td>
                                            {customerApproval.resolvedTime
                                                ? new Date(customerApproval.resolvedTime).toLocaleString()
                                                : <span className="text-muted">—</span>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th className="text-muted">Last updated by</th>
                                        {/* Round DMS-UAT-2 — name comes along with the ID */}
                                        <td>
                                            {customerApproval.lastUpdatedBy != null
                                                ? (customerApproval.lastUpdatedByName
                                                    ? <>{customerApproval.lastUpdatedByName} <span className="text-muted">(ID: {customerApproval.lastUpdatedBy})</span></>
                                                    : customerApproval.lastUpdatedBy)
                                                : <span className="text-muted">—</span>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th className="text-muted">Last updated</th>
                                        <td>
                                            {customerApproval.lastUpdatedTime
                                                ? new Date(customerApproval.lastUpdatedTime).toLocaleString()
                                                : <span className="text-muted">—</span>}
                                        </td>
                                    </tr>
                                    {/* Round R9 (2026) — Q17 — surface notes
                                        in read-only view. Either field can
                                        be absent (older docs predating R9);
                                        the row is shown anyway with a dim
                                        placeholder so the column structure
                                        stays consistent. */}
                                    <tr>
                                        <th className="text-muted">Customer approval notes</th>
                                        <td style={{ whiteSpace: "pre-wrap" }}>
                                            {customerApproval.customerApprovalNotes
                                                || document?.ecnDetails?.[0]?.customerApprovalNotes
                                                || <span className="text-muted">—</span>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th className="text-muted">Customer notification notes</th>
                                        <td style={{ whiteSpace: "pre-wrap" }}>
                                            {customerApproval.customerNotificationNotes
                                                || document?.ecnDetails?.[0]?.customerNotificationNotes
                                                || <span className="text-muted">—</span>}
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>
                        </Col>
                    </Row>
                </>
            )}
            {/* Go-live parity (2026-09-24): DMS#22 — legacy ECO notes panel
                (ECO notes + customer approval / notification received). */}
            {document?.ecnDetails?.[0] && (
                <EcoNotesPanel
                    dmsId={dmsId}
                    ecn={document.ecnDetails[0]}
                    canEdit={isDocControl}
                    onSaved={(saved) => {
                        setDocument((prev) => (prev?.ecnDetails?.[0]
                            ? { ...prev, ecnDetails: [{ ...prev.ecnDetails[0], ...saved }, ...prev.ecnDetails.slice(1)] }
                            : prev));
                        // The printed ECN carries the ECO notes — store a matching PDF.
                        refreshStoredDmsPdf(dmsId, api, document?.header?.logoType);
                    }}
                />
            )}
        </TabPane>
    );

    // Phase 2.7 — Customer Approval inline editor
    // ---------------------------------------------
    // Surfaces the PUT /api/dms/{id}/customer-approval endpoint as a form.
    // The backend enforces the CustNone XOR (required-flags) mutual exclusion
    // and computes resolved-state stamps; we mirror that XOR client-side
    // to give immediate feedback without waiting for a round-trip error.
    const renderCustomerApprovalEditor = () => {
        const xorViolation = caForm.custNone && (caForm.custApprovalRequired || caForm.custNotificationRequired);
        return (
            <>
                <Row className="g-3">
                    <Col xs={12}>
                        <h6 className="fw-semibold mb-3">
                            <i className="ri-edit-line align-bottom me-1 text-primary"></i>
                            Edit customer approval
                        </h6>
                    </Col>
                    <Col md={6}>
                        <div className="border rounded p-3">
                            <div className="fw-semibold mb-2 text-muted small">REQUIREMENTS</div>
                            <div className="form-check mb-2">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="caCustApprovalRequired"
                                    checked={caForm.custApprovalRequired}
                                    onChange={(e) => setCaForm((p) => ({ ...p, custApprovalRequired: e.target.checked }))}
                                />
                                <label className="form-check-label" htmlFor="caCustApprovalRequired">
                                    Customer approval required
                                </label>
                            </div>
                            <div className="form-check mb-2">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="caCustNotificationRequired"
                                    checked={caForm.custNotificationRequired}
                                    onChange={(e) => setCaForm((p) => ({ ...p, custNotificationRequired: e.target.checked }))}
                                />
                                <label className="form-check-label" htmlFor="caCustNotificationRequired">
                                    Customer notification required
                                </label>
                            </div>
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="caCustNone"
                                    checked={caForm.custNone}
                                    onChange={(e) => setCaForm((p) => ({ ...p, custNone: e.target.checked }))}
                                />
                                <label className="form-check-label" htmlFor="caCustNone">
                                    No customer involvement
                                </label>
                            </div>
                        </div>
                    </Col>
                    <Col md={6}>
                        <div className="border rounded p-3">
                            <div className="fw-semibold mb-2 text-muted small">STATUS</div>
                            <div className="form-check mb-2">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="caApprovalObtained"
                                    checked={caForm.approvalObtained}
                                    disabled={!caForm.custApprovalRequired}
                                    onChange={(e) => setCaForm((p) => ({ ...p, approvalObtained: e.target.checked }))}
                                />
                                <label className="form-check-label" htmlFor="caApprovalObtained">
                                    Customer approval obtained
                                    {!caForm.custApprovalRequired && (
                                        <span className="text-muted small ms-2">(check "approval required" first)</span>
                                    )}
                                </label>
                            </div>
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="caNotificationProvided"
                                    checked={caForm.notificationProvided}
                                    disabled={!caForm.custNotificationRequired}
                                    onChange={(e) => setCaForm((p) => ({ ...p, notificationProvided: e.target.checked }))}
                                />
                                <label className="form-check-label" htmlFor="caNotificationProvided">
                                    Customer notification provided
                                    {!caForm.custNotificationRequired && (
                                        <span className="text-muted small ms-2">(check "notification required" first)</span>
                                    )}
                                </label>
                            </div>
                        </div>
                    </Col>
                    {/* Round R9 (2026) — Q17 — free-text notes for the
                        resolver. Persisted to DmsEcr.customerApprovalNotes
                        / customerNotificationNotes server-side. Each
                        textarea is enabled only when the matching "required"
                        flag is set — matches the existing checkbox
                        gating pattern for the resolution checkboxes. */}
                    <Col md={6}>
                        <Label for="caApprovalNotes" className="small fw-semibold mb-1">
                            Customer approval notes
                            {!caForm.custApprovalRequired && (
                                <span className="text-muted ms-2">(check "approval required" first)</span>
                            )}
                        </Label>
                        <Input
                            type="textarea"
                            id="caApprovalNotes"
                            rows={3}
                            maxLength={2000}
                            value={caForm.customerApprovalNotes ?? ""}
                            disabled={!caForm.custApprovalRequired || caSaving}
                            onChange={(e) => setCaForm((p) => ({ ...p, customerApprovalNotes: e.target.value }))}
                            placeholder="e.g. PO #12345 approved via email from John Doe on 2026-05-20"
                        />
                    </Col>
                    <Col md={6}>
                        <Label for="caNotificationNotes" className="small fw-semibold mb-1">
                            Customer notification notes
                            {!caForm.custNotificationRequired && (
                                <span className="text-muted ms-2">(check "notification required" first)</span>
                            )}
                        </Label>
                        <Input
                            type="textarea"
                            id="caNotificationNotes"
                            rows={3}
                            maxLength={2000}
                            value={caForm.customerNotificationNotes ?? ""}
                            disabled={!caForm.custNotificationRequired || caSaving}
                            onChange={(e) => setCaForm((p) => ({ ...p, customerNotificationNotes: e.target.value }))}
                            placeholder="e.g. Sent FYI email to procurement@customer.example on 2026-05-20"
                        />
                    </Col>
                    {xorViolation && (
                        <Col xs={12}>
                            <div className="alert alert-warning mb-0 py-2 small">
                                <i className="ri-error-warning-line me-1"></i>
                                "No customer involvement" can't be combined with any required flag.
                                Uncheck one before saving.
                            </div>
                        </Col>
                    )}
                    {caSaveError && (
                        <Col xs={12}>
                            <div className="alert alert-danger mb-0 py-2 small">{caSaveError}</div>
                        </Col>
                    )}
                    <Col xs={12} className="d-flex justify-content-end gap-2">
                        <button
                            type="button"
                            className="btn btn-light btn-sm"
                            onClick={cancelCustomerApprovalEdit}
                            disabled={caSaving}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-success btn-sm"
                            onClick={saveCustomerApproval}
                            disabled={caSaving || xorViolation}
                        >
                            {caSaving ? (<><Spinner size="sm" /> Saving…</>) : "Save"}
                        </button>
                    </Col>
                </Row>
            </>
        );
    };

    // ───────────────────────────────────────────────────────────────────
    // Round DMS-UAT-1 (2026) — Issue 15/16: shared Properties tab renderer
    // (previously duplicated inline for ECN tab 6 and non-ECN tab 9).
    //
    //   • Shows Author (name + EmpID) and Manager — Issue 16's "author
    //     name does not show" gap.
    //   • Approvals list shows name, EmpID, three-state status, and the
    //     full approval timestamp — Issue 15.
    //   • Notification list shows name + EmpID — Issue 15.
    //   • Edit mode (authorized users only; backend re-checks and audits)
    //     adds/removes approvers (Status 0/60 only — approval history is
    //     frozen afterwards) and notification recipients (Document
    //     Control at any status).
    // ───────────────────────────────────────────────────────────────────
    const renderPropertiesTab = (tabId) => {
        // Round DMS-Legacy-Parity-1d: a shared Saved document (99) may be
        // continued by any DMS user (the backend gates the write on dms.create
        // and makes the person saving the author); a private Draft (60) only
        // by its author / Document Control.
        const isDraftHere = status === 60 || status === 99;
        const isAuthorHere = Number(document?.header?.author) === empId || status === 99;
        const canEditApprovers =
            (status === 0 || isDraftHere) && (isDocControl || isAuthorHere);
        const canEditNotifications =
            isDocControl || ((status === 0 || status === 50 || isDraftHere) && isAuthorHere);
        const canEditProps = canEditApprovers || canEditNotifications;

        return (
            <TabPane tabId={tabId}>
                <div className="d-flex flex-wrap align-items-start justify-content-between mb-3 gap-2">
                    <div>
                        <div className="text-muted small">Author</div>
                        <div className="fw-semibold">
                            {document.header.authorName
                                ? <>{document.header.authorName} <span className="text-muted fw-normal">(ID: {document.header.author})</span></>
                                : document.header.author}
                        </div>
                        {Boolean(document.header.manager) && document.header.manager !== 0 && (
                            <div className="text-muted small mt-1">
                                Manager:{" "}
                                {document.header.managerName
                                    ? <>{document.header.managerName} (ID: {document.header.manager})</>
                                    : document.header.manager}
                            </div>
                        )}
                    </div>
                    {canEditProps && (
                        <Button
                            color={propsEditMode ? "light" : "primary"}
                            size="sm"
                            outline={!propsEditMode}
                            onClick={() => { setPropsEditMode((v) => !v); setPropsError(null); }}
                            disabled={propsBusy}
                        >
                            <i className={`${propsEditMode ? "ri-close-line" : "ri-edit-line"} align-bottom me-1`}></i>
                            {propsEditMode ? "Done" : "Edit"}
                        </Button>
                    )}
                </div>

                {propsError && (
                    <div className="alert alert-danger py-2 small">{propsError}</div>
                )}

                <Row className="g-4">
                    <Col md={6}>
                        <h6 className="fw-semibold mb-3">Approvals</h6>

                        <Table hover bordered size="sm">
                            <thead className="table-light">
                                <tr>
                                    <th>Approver</th>
                                    <th>Status</th>
                                    <th>Timestamp</th>
                                    {propsEditMode && canEditApprovers && <th style={{ width: 44 }}></th>}
                                </tr>
                            </thead>

                            <tbody>
                                {document.approvals?.length ? (
                                    document.approvals.map((a) => (
                                        <tr key={a.approver}>
                                            <td>
                                                <div>{a.approverName ?? "Unknown"}</div>
                                                <small className="text-muted">ID: {a.approver}</small>
                                            </td>
                                            <td>
                                                {a.approved === true ? (
                                                    <span className="text-success">Approved</span>
                                                ) : a.approved === false ? (
                                                    <span className="text-danger">Denied</span>
                                                ) : (
                                                    <span className="text-warning">Pending</span>
                                                )}
                                            </td>
                                            <td>
                                                {a.timeApproved
                                                    ? new Date(a.timeApproved).toLocaleString()
                                                    : <span className="text-muted">—</span>}
                                            </td>
                                            {propsEditMode && canEditApprovers && (
                                                <td className="text-center">
                                                    {(a.approved === null || a.approved === undefined) && (
                                                        <Button
                                                            color="danger" size="sm" outline
                                                            title="Remove this approver"
                                                            disabled={propsBusy}
                                                            onClick={() => removeApprover(a.approver)}
                                                        >
                                                            <i className="ri-delete-bin-line"></i>
                                                        </Button>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={propsEditMode && canEditApprovers ? 4 : 3} className="text-muted text-center">
                                            {/* Go-live parity (2026-09-24): DMS#23 — QSI-imported approval history */}
                                            {document?.qsiHistory?.approvalText
                                                ? <div className="text-start text-body" style={{ whiteSpace: "pre-wrap" }}>{document.qsiHistory.approvalText}</div>
                                                : "No approvals"}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>

                        {propsEditMode && canEditApprovers && (
                            <div className="d-flex gap-2 mt-2">
                                <Input
                                    bsSize="sm"
                                    placeholder="Approver EmpID"
                                    value={newApproverId}
                                    onChange={(e) => setNewApproverId(e.target.value)}
                                    disabled={propsBusy}
                                    style={{ maxWidth: 160 }}
                                />
                                <Button color="primary" size="sm" onClick={addApprover} disabled={propsBusy}>
                                    <i className="ri-add-line align-bottom me-1"></i>Add approver
                                </Button>
                            </div>
                        )}
                        {propsEditMode && !canEditApprovers && (
                            <div className="text-muted small mt-2">
                                The approver list is frozen once the document leaves the
                                Draft/Pending stage.
                            </div>
                        )}
                    </Col>

                    <Col md={6}>
                        <h6 className="fw-semibold mb-3">Electronic Notification List</h6>

                        <ul className="list-group">
                            {document.notifications?.length ? (
                                document.notifications.map((n) => (
                                    <li key={n.empid} className="list-group-item d-flex justify-content-between align-items-center">
                                        <span>{n.empName ?? "Unknown"}</span>
                                        <span className="d-flex align-items-center gap-2">
                                            <span className="text-muted">ID: {n.empid}</span>
                                            {propsEditMode && canEditNotifications && (
                                                <Button
                                                    color="danger" size="sm" outline
                                                    title="Remove from notification list"
                                                    disabled={propsBusy}
                                                    onClick={() => removeNotification(n.empid)}
                                                >
                                                    <i className="ri-delete-bin-line"></i>
                                                </Button>
                                            )}
                                        </span>
                                    </li>
                                ))
                            ) : (
                                <li className="list-group-item text-muted">
                                    No notifications
                                </li>
                            )}
                        </ul>

                        {propsEditMode && canEditNotifications && (
                            <div className="d-flex gap-2 mt-2">
                                <Input
                                    bsSize="sm"
                                    placeholder="Employee EmpID"
                                    value={newNotifyId}
                                    onChange={(e) => setNewNotifyId(e.target.value)}
                                    disabled={propsBusy}
                                    style={{ maxWidth: 160 }}
                                />
                                <Button color="primary" size="sm" onClick={addNotification} disabled={propsBusy}>
                                    <i className="ri-add-line align-bottom me-1"></i>Add recipient
                                </Button>
                            </div>
                        )}
                    </Col>
                </Row>
            </TabPane>
        );
    };

    // Owner 2026-10-12: the open document shows in the topbar trail.
    useTrailLeaf(
        isOpen && document?.header
            ? [{ label: `Document ${displayDocId(document.header.docID, document.header.docType) || dmsId}` }]
            : null,
        5
    );

    return (
        <>
        <Modal isOpen={isOpen} toggle={toggle} size="xl" centered className="ecn-modal">
            <ModalHeader toggle={toggle} className="border-bottom bg-light">
                <div className="d-flex align-items-center justify-content-between w-100" style={{ gap: 12, minWidth: 0 }}>
                    {/* Owner 2026-10-12: a long title ran under the buttons; it now takes the room
                        that is left and clips with an ellipsis (the full title is in the tooltip). */}
                    <div className="d-flex flex-column" style={{ minWidth: 0, flex: "1 1 auto" }}>
                        <span className="fw-bold fs-5 text-truncate">Document {displayDocId(document?.header?.docID, document?.header?.docType)}</span>
                        <small className="text-muted text-truncate" title={document?.header?.docTitle || ""}>{document?.header?.docTitle}</small>
                    </div>
                    {/* Round R12 (2026) — Edit + Print buttons in the modal
                        header, matching legacy WinForms' top-of-window
                        toolbar (Image 3 in your screenshots). These are
                        ALWAYS visible — the existing R4 action bar
                        (Approve/Deny/Release/etc.) renders below in the
                        body and is gated by doc status + role. Edit is
                        further gated to editable statuses (0/60). */}
                    {document && (
                        <div className="d-flex gap-2 me-3 flex-shrink-0">
                            {/* Round DMS-UAT-22 — printable reports (legacy parity with the
                                dmsApproverRpt / dmsRevLogRpt / dmsAttachmentsRpt / DmsAssociatedRpt
                                Crystal reports). Same auth-aware blob download as Print PDF. */}
                            <UncontrolledDropdown size="sm">
                                <DropdownToggle color="secondary" size="sm" outline caret>
                                    <i className="ri-file-list-3-line me-1"></i> Reports
                                </DropdownToggle>
                                <DropdownMenu>
                                    {[
                                        ["approvals", "Approval History"],
                                        ["revisions", "Revision Log"],
                                        ["attachments", "Attachments"],
                                        ["associated", "Associated Documents"],
                                    ].map(([kind, label]) => (
                                        <DropdownItem key={kind} onClick={async () => {
                                            // Owner (2026-10-01): always a PDF, built here from the report's rows
                                            // (the server's Chromium print does not run on the App Service and
                                            // used to hand out HTML pages). Same engine as Print PDF.
                                            // Owner (2026-10-02): shown in the print preview first; Print and
                                            // Download are there.
                                            try {
                                                setPreviewBusy(true);
                                                const res = await api.get(`/dms/${dmsId}/reports/${kind}`, { params: { format: "json" } });
                                                const report = res?.data || {};
                                                const pdf = buildDmsReportDoc(report, document?.header?.logoType || "sswhite");
                                                showPreview(pdf.output("blob"),
                                                    `${label} — ${document?.header?.docID || ""}`,
                                                    dmsReportFileName({ ...report, kind }));
                                            } catch (err) {
                                                toast.error(err?.response?.data?.error || err?.message || "The report could not be built.");
                                            } finally {
                                                setPreviewBusy(false);
                                            }
                                        }}>{label}</DropdownItem>
                                    ))}
                                </DropdownMenu>
                            </UncontrolledDropdown>
                            <Button
                                color="info"
                                size="sm"
                                outline
                                disabled={previewBusy}
                                onClick={async () => {
                                    // Owner (2026-10-02): see the document before printing. Built from
                                    // the live data like Print PDF (nothing is stored); the last stored
                                    // copy is the fallback when the build fails.
                                    const logo = document?.header?.logoType || "sswhite";
                                    const docId = document?.header?.docID || "document";
                                    const fileName = `${docId}-Rev${document?.header?.rev ?? 0}.pdf`;
                                    const title = `Print Preview — ${displayDmsLabel(document)}`;
                                    setPreviewBusy(true);
                                    try {
                                        const { doc } = await buildDmsPdfFromApi(dmsId, api, logo);
                                        showPreview(doc.output("blob"), title, fileName);
                                    } catch (buildErr) {
                                        console.error("DMS PDF build failed, previewing the stored copy:", buildErr);
                                        try {
                                            const res = await api.get(`/dms/${dmsId}/pdf`, { responseType: "blob" });
                                            showPreview(new Blob([res.data], { type: "application/pdf" }), title, fileName);
                                            toast.warn("The PDF could not be rebuilt; showing the last stored copy.");
                                        } catch (err) {
                                            toast.error(err?.message || "The preview could not be generated.");
                                        }
                                    } finally {
                                        setPreviewBusy(false);
                                    }
                                }}
                                title="See the document before printing"
                            >
                                {previewBusy
                                    ? <Spinner size="sm" className="me-1" />
                                    : <i className="ri-eye-line align-bottom me-1"></i>}
                                Print Preview
                            </Button>
                            <Button
                                color="primary"
                                size="sm"
                                outline
                                onClick={async () => {
                                    // Phase 9 stored the browser-built PDF and served the stored copy on
                                    // every later click. Owner finding (2026-10-01, WI-17132): a revision
                                    // log line added later, or a body saved in the legacy app, never
                                    // reached the printout because the frozen copy was served. Now every
                                    // click builds the PDF from the live data, stores the fresh copy (so
                                    // the mails reuse it) and downloads it; the stored copy is only the
                                    // fallback when the build itself fails. If storing is refused (only
                                    // author / manager / supervisor / Document Control may store), the
                                    // locally built PDF is still downloaded.
                                    const logo = document?.header?.logoType || "sswhite";
                                    const fileName = `${document?.header?.docID || "document"}-Rev${document?.header?.rev ?? 0}.pdf`;
                                    const download = (blob) => {
                                        const url = window.URL.createObjectURL(blob);
                                        const a = window.document.createElement("a");
                                        a.href = url;
                                        a.download = fileName;
                                        window.document.body.appendChild(a);
                                        a.click();
                                        window.document.body.removeChild(a);
                                        window.URL.revokeObjectURL(url);
                                    };
                                    const blobError = async (err) => {
                                        try {
                                            const text = await err?.response?.data?.text?.();
                                            return text ? JSON.parse(text) : null;
                                        } catch { return null; }
                                    };
                                    try {
                                        toast.info("Building the PDF…");
                                        let doc;
                                        try {
                                            doc = (await uploadDmsPdf(dmsId, api, logo)).doc;
                                        } catch (upErr) {
                                            console.warn("DMS PDF built but not stored:", upErr?.response?.data || upErr);
                                            doc = (await buildDmsPdfFromApi(dmsId, api, logo)).doc;
                                        }
                                        download(doc.output("blob"));
                                    } catch (buildErr) {
                                        console.error("DMS PDF build failed, serving the stored copy:", buildErr);
                                        try {
                                            const res = await api.get(`/dms/${dmsId}/pdf`, { responseType: "blob" });
                                            download(new Blob([res.data], { type: "application/pdf" }));
                                            toast.warn("The PDF could not be rebuilt; the last stored copy was downloaded.");
                                        } catch (err) {
                                            const body = await blobError(err);
                                            toast.error(body?.error || err?.message || "The PDF could not be generated.");
                                        }
                                    }
                                }}
                                title="Download PDF"
                            >
                                <i className="ri-printer-line align-bottom me-1"></i>
                                Print PDF
                            </Button>
                            {/* Edit when status allows it (0=Pending, 60=Draft) for
                                the author path, and — Round DMS-UAT-1, Issue 6/11/16 —
                                at ANY status for Document Control (isDmsManager /
                                isItAdmin). The backend PUT enforces the same rule
                                server-side, preserves the frozen approval record on
                                non-mutable docs, and audit-logs the override edit. */}
                            {/* Round DMS-Legacy-Parity-5 (owner): editing is an extra privilege
                                once submitted — Draft: author; Saved: everyone; Pending (0):
                                document manager / author's supervisor / Document Control (not
                                the author); Needs Revision (50): author + manager + supervisor
                                + DC. The backend PUT enforces the same rule. */}
                            {(() => {
                                const st = document?.header?.status;
                                const author = Number(document?.header?.author);
                                const manager = Number(document?.header?.manager);
                                const supervisor = Number(document?.header?.authorSupervisor);
                                const meIsAuthor = author === empId;
                                const meIsManager = manager === empId;
                                const meIsSupervisor = supervisor > 0 && supervisor === empId;
                                if (isDocControl) return true;
                                if (st === 99) return true;
                                if (st === 60) return meIsAuthor;
                                if (st === 0) return meIsManager || meIsSupervisor;
                                if (st === 50) return meIsAuthor || meIsManager || meIsSupervisor;
                                return false;
                            })() && (
                                <Button
                                    color="secondary"
                                    size="sm"
                                    outline
                                    onClick={() => {
                                        const slug = DOCTYPE_SLUG[document?.header?.docType] || "ecn";
                                        toggle();   // close the read-only modal
                                        navigate(`/dms/edit/${slug}/${dmsId}`);
                                    }}
                                    title={
                                        (document?.header?.status === 0 || document?.header?.status === 60 || document?.header?.status === 99)
                                            ? "Open in editor"
                                            : "Open in editor (Document Control override — audited)"
                                    }
                                >
                                    <i className="ri-pencil-line align-bottom me-1"></i>
                                    Edit
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </ModalHeader>

            <ModalBody className="p-4">
                {loading ? (
                    <div className="d-flex align-items-center gap-2 text-muted">
                        <Spinner size="sm" />
                        Loading document...
                    </div>
                ) : !document ? (
                    <EmptyState compact icon={loadError ? "ri-archive-line" : "ri-file-search-line"}
                        title={loadError ? "Document unavailable" : "No data found"}
                        message={loadError || "This document could not be loaded."} />
                ) : (
                    <>
                        {/* ─────────────────────────────────────────────────
                            Round R4 (2026) — Manager Action Bar.

                            Visible only when the document state + caller
                            permissions yield at least one available action
                            (see visibleActionKeysForStatus + ACTION_DEFS at
                            the top of this file). All buttons funnel through
                            a single confirmation modal that:
                              • shows the legacy-verbatim wording where
                                applicable (Q19's take-over wording is
                                preserved exactly)
                              • for /release, captures the Implementation Date
                                like AwaitingRelease.js does
                              • blocks during in-flight POST + surfaces
                                backend errors inline

                            All R4 endpoints honor the Round-R3 caller-identity
                            guards server-side, so this UI cannot be used to
                            spoof an approver / releaser even with a hand-
                            crafted request.
                        ───────────────────────────────────────────────────── */}
                        {visibleActionKeys.length > 0 && (
                            <div className="d-flex flex-wrap gap-2 align-items-center mb-3 p-2 border rounded bg-light-subtle">
                                <div className="me-auto fw-semibold text-muted small">
                                    <i className="ri-tools-line align-bottom me-1"></i>
                                    Document Actions
                                </div>
                                {visibleActionKeys.map((key) => {
                                    const def = ACTION_DEFS[key];
                                    return (
                                        <Button
                                            key={key}
                                            color={def.tone}
                                            size="sm"
                                            outline
                                            onClick={() => openConfirm(key)}
                                            title={def.label}
                                        >
                                            <i className={`${def.icon} align-bottom me-1`}></i>
                                            {def.label}
                                        </Button>
                                    );
                                })}
                            </div>
                        )}

                        <Nav pills className="mb-4 ecn-tabs">
                            {tabs.map((tab) => (
                                <NavItem key={tab.id}>
                                    <NavLink
                                        active={activeTab === tab.id}
                                        onClick={() => toggleTab(tab.id)}
                                    >
                                        {tab.label}
                                    </NavLink>
                                </NavItem>
                            ))}
                        </Nav>

                        <TabContent activeTab={activeTab}>
                            {/* ===================== ECN ===================== */}

                            {docType === 4 ? (
                                <>
                                    {/* ECN HEADER */}
                                    <TabPane tabId="1">

                                        <Row className="g-4">
                                            <Col md={4}>
                                                <div className="ecn-field">
                                                    <div className="ecn-label">Title</div>
                                                    <div className="ecn-value">{ecrFinalTitle}</div>
                                                </div>
                                            </Col>

                                            <Col md={4}>
                                                <div className="ecn-field">
                                                    <div className="ecn-label">Rev</div>
                                                    <div className="ecn-value">{formatRev(document.header.rev)}</div>
                                                </div>
                                            </Col>

                                            <Col md={4}>
                                                <div className="ecn-field">
                                                    <div className="ecn-label">Status</div>
                                                    {/* Round DMS-UAT-1 — Issue 19: show the label, not the raw code */}
                                                    <div className="ecn-value">{document.header.statusLabel ?? document.header.status}</div>
                                                </div>
                                            </Col>

                                            <Col md={4}>
                                                <div className="ecn-field">
                                                    <div className="ecn-label">Author</div>
                                                    {/* Round DMS-UAT-1 — Issue 5: name + EmpID, not just the ID */}
                                                    <div className="ecn-value">
                                                        {document.header.authorName
                                                            ? <>{document.header.authorName} <span className="text-muted">(ID: {document.header.author})</span></>
                                                            : document.header.author}
                                                    </div>
                                                </div>
                                            </Col>

                                            <Col md={4}>
                                                <div className="ecn-field">
                                                    <div className="ecn-label">Created</div>
                                                    <div className="ecn-value">
                                                        {document.header.createdTime?.slice(0, 10)}
                                                    </div>
                                                </div>
                                            </Col>

                                            <Col md={4}>
                                                <div className="ecn-field">
                                                    <div className="ecn-label">Implementation Date</div>
                                                    {/* Round DMS-UAT-1 — bug fix: this cell previously
                                                        displayed createdTime. It now shows the actual
                                                        ImplementationDate (blank until released). */}
                                                    <div className="ecn-value">
                                                        {document.header.implementationDate?.slice(0, 10) || "—"}
                                                    </div>
                                                </div>
                                            </Col>

                                        </Row>
                                    </TabPane>

                                    {/* PRODUCT INFO */}
                                    <TabPane tabId="2">
                                        {/* Round DMS-UAT-1 — Issue 7: ordered one-row-per-part grid
                                            instead of six lumped multiline fields. Parsing happens on
                                            display only; stored data is untouched. */}
                                        {(() => {
                                            const partRows = parseProductInfoRows(document.productInfo);
                                            if (partRows.length === 0) {
                                                return (
                                                    <div className="text-muted text-center py-4">
                                                        <i className="ri-box-3-line" style={{ fontSize: 28 }}></i>
                                                        <div className="mt-2">No product information entered.</div>
                                                    </div>
                                                );
                                            }
                                            return (
                                                <Table hover bordered responsive size="sm" className="align-middle mb-0">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th style={{ width: 36 }}>#</th>
                                                            {PRODUCT_INFO_FIELDS.map((f) => (
                                                                <th key={f.key}>{f.label}</th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {partRows.map((row, i) => (
                                                            <tr key={i}>
                                                                <td className="text-muted">{i + 1}</td>
                                                                {PRODUCT_INFO_FIELDS.map((f) => (
                                                                    <td key={f.key}>{row[f.key] || <span className="text-muted">—</span>}</td>
                                                                ))}
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            );
                                        })()}
                                    </TabPane>

                                    {/* ECN DETAILS */}
                                    {/* Document Manager 2026-10-13: the four side-by-side columns (Title,
                                        Description, Reason, Implementation) squeezed the change table into a
                                        narrow strip; the sections now follow one another, titled as on the
                                        printed ECN. */}
                                    <TabPane tabId="3">
                                        {!document.ecnDetails || document.ecnDetails.length === 0 ? (
                                            <div className="text-muted text-center py-4">No ECN details</div>
                                        ) : (
                                            document.ecnDetails.map((x, index) => {
                                                // Round DMS-UAT-1 — Issue 9: normalize stored
                                                // tokens (legacy "Use to Deplation" etc.) so
                                                // saved options still render checked.
                                                const selectedChanges = normalizeWillChangeValues(x.ecrWillChange);
                                                const html = (v) => (v && String(v).trim() ? v : "");
                                                const section = (title, value) => (
                                                    <div className="ecn-section">
                                                        <div className="ecn-section-title">{title}</div>
                                                        {html(value)
                                                            ? <div className="ecn-section-body ecn-text-block" dangerouslySetInnerHTML={{ __html: value }} />
                                                            : <div className="ecn-section-body text-muted">—</div>}
                                                    </div>
                                                );
                                                return (
                                                    <div key={index} className="ecn-details-stack">
                                                        <div className="ecn-section">
                                                            <div className="ecn-section-title">Title</div>
                                                            <div className="ecn-section-body">{x.ecrTitle || <span className="text-muted">—</span>}</div>
                                                        </div>
                                                        {section("Description of Change", x.descriptionOfChange)}
                                                        {section("Reason for Change", x.reasonForChange)}
                                                        <div className="ecn-section">
                                                            <div className="ecn-section-title">ECN Will Change</div>
                                                            <EcnWillChangeGrid checked={selectedChanges} />
                                                        </div>
                                                        {section("Instructions for ECN Implementation", x.implementationDateInstructions)}
                                                    </div>
                                                );
                                            })
                                        )}
                                    </TabPane>

                                    {/* ASSOCIATED DOCS */}
                                    <TabPane tabId="4">
                                        <Table hover bordered responsive className="align-middle">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Number</th>
                                                    <th>Rev</th>
                                                    <th>Doc Title</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {document.associatedDocs?.length ? (
                                                    document.associatedDocs.map((doc) => (
                                                        <tr key={`${doc.docId}-${doc.rev}`}>
                                                            <td>{doc.docId}</td>
                                                            <td>{formatRev(doc.rev)}</td>
                                                            <td>{doc.title}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="3" className="text-muted text-center">
                                                            {/* Go-live parity (2026-09-24): DMS#23 — QSI-imported associated list */}
                                                            {document.qsiHistory?.associatedText
                                                                ? <div className="text-start text-body" style={{ whiteSpace: "pre-wrap" }}>{document.qsiHistory.associatedText}</div>
                                                                : "No associated documents"}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </TabPane>

                                    {/* ATTACHMENTS */}
                                    <TabPane tabId="5">
                                        <Table hover bordered responsive className="align-middle">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>File Name</th>
                                                    <th>Title</th>
                                                    <th>Subject To Approval</th>
                                                    <th>Download</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {document.attachments?.length ? (
                                                    document.attachments.map((a, i) => (
                                                        <tr key={i}>
                                                            <td>{a.fileName}</td>
                                                            <td>
                                                                {a.attachmentTitle}
                                                                {/* Round DMS-UAT-22 — legacy in-grid title edit */}
                                                                <Button color="link" size="sm" className="p-0 ms-2" title="Rename attachment title"
                                                                    onClick={async () => {
                                                                        const next = window.prompt("Attachment title", a.attachmentTitle || "");
                                                                        if (next === null) return;
                                                                        try {
                                                                            await api.put(`/dms/${dmsId}/attachments/title`, { fileName: a.fileName, attachmentTitle: next });
                                                                            toast.success("Attachment title updated.");
                                                                            a.attachmentTitle = next;
                                                                            if (typeof refetchDocument === "function") refetchDocument();
                                                                        } catch (err) {
                                                                            toast.error(err?.response?.data?.error || err?.message || "Rename failed.");
                                                                        }
                                                                    }}>
                                                                    <i className="ri-pencil-line"></i>
                                                                </Button>
                                                            </td>
                                                            <td>{a.subjectToApproval ? "Yes" : "No"}</td>
                                                            <td>
                                                                {/* Owner finding (2026-10-01): a plain link to the api route landed on
                                                                    the dashboard home (the site router owns that path, and a link
                                                                    cannot carry the sign-in). Fetch with the sign-in and save, like
                                                                    the edit form does. */}
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-link p-0"
                                                                    title={`Download ${a.fileName || "attachment"}`}
                                                                    onClick={() => downloadDmsAttachment(a).catch((err) =>
                                                                        toast.error(err?.response?.data?.error || err?.message || "Attachment download failed."))}
                                                                >
                                                                    <FaDownload />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="4" className="text-muted text-center">
                                                            No attachments
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </TabPane>

                                    {/* PROPERTIES — Round DMS-UAT-1: shared renderer with
                                        author display + authorized edit (Issue 15/16) */}
                                    {renderPropertiesTab("6")}

                                    {/* Phase 2.6 — Revisions + Customer Approval tabs (ECN) */}
                                    {renderRevisionsTab("7")}
                                    {renderCustomerApprovalTab("8")}

                                    {/* Round R8 (2026) — ECN Tasks subgrid (Gap 9).
                                        Restored from legacy DMSNew.vb dgvEcrTask. The
                                        Tasks tab is ECN-only; this TabPane lives inside
                                        the docType===4 branch. See
                                        ECN/tabs/EcnTasksTab.js for the full subgrid. */}
                                    <TabPane tabId="9">
                                        <EcnTasksTab
                                            dmsId={dmsId}
                                            docType={4}
                                            author={document.header?.author}
                                            approvals={document.approvals}
                                        />
                                    </TabPane>
                                </>
                            ) : (
                                <>
                                    {/* NON-ECN HEADER */}
                                    <TabPane tabId="1">
                                        <Row className="g-4">
                                            <Col md={4}>
                                                <div className="ecn-field">
                                                    <div className="ecn-label">Title</div>
                                                    <div className="ecn-value">{document.header.docTitle}</div>
                                                </div>
                                            </Col>

                                            <Col md={4}>
                                                <div className="ecn-field">
                                                    <div className="ecn-label">Rev</div>
                                                    <div className="ecn-value">{formatRev(document.header.rev)}</div>
                                                </div>
                                            </Col>

                                            <Col md={4}>
                                                <div className="ecn-field">
                                                    <div className="ecn-label">Status</div>
                                                    {/* Round DMS-UAT-1 — Issue 19: label, not raw code */}
                                                    <div className="ecn-value">{document.header.statusLabel ?? document.header.status}</div>
                                                </div>
                                            </Col>

                                            <Col md={4}>
                                                <div className="ecn-field">
                                                    <div className="ecn-label">Author</div>
                                                    {/* Round DMS-UAT-1 — Issue 5: name + EmpID */}
                                                    <div className="ecn-value">
                                                        {document.header.authorName
                                                            ? <>{document.header.authorName} <span className="text-muted">(ID: {document.header.author})</span></>
                                                            : document.header.author}
                                                    </div>
                                                </div>
                                            </Col>

                                            <Col md={4}>
                                                <div className="ecn-field">
                                                    <div className="ecn-label">Created</div>
                                                    <div className="ecn-value">
                                                        {document.header.createdTime?.slice(0, 10)}
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md={4}>
                                                <div className="ecn-field">
                                                    <div className="ecn-label">Implementation Date</div>
                                                    {/* Round DMS-UAT-1 — bug fix: was createdTime */}
                                                    <div className="ecn-value">
                                                        {document.header.implementationDate?.slice(0, 10) || "—"}
                                                    </div>
                                                </div>
                                            </Col>
                                            {document.header.docType === 5 && (
                                                <Col md={4}>
                                                    <div className="ecn-field">
                                                        <div className="ecn-label">Literature Type</div>
                                                        <div className="ecn-value">
                                                            {document.header.litType || ""}
                                                        </div>
                                                    </div>
                                                </Col>
                                            )}
                                        </Row>
                                    </TabPane>

                                    {/* PURPOSE */}
                                    <TabPane tabId="2">
                                        <div
                                            className="dms-text-block"
                                            dangerouslySetInnerHTML={{ __html: document.header?.purpose }}
                                        />
                                    </TabPane>

                                    {/* SCOPE */}
                                    <TabPane tabId="3">
                                        <div
                                            className="dms-text-block"
                                            dangerouslySetInnerHTML={{ __html: document.header?.scope }}
                                        />
                                    </TabPane>

                                    {/* RESPONSIBILITIES */}
                                    <TabPane tabId="4">
                                        <div
                                            className="dms-text-block"
                                            dangerouslySetInnerHTML={{ __html: document.header?.responsibilities }}
                                        />
                                    </TabPane>

                                    {/* DEFINITIONS */}
                                    <TabPane tabId="5">
                                        <div
                                            className="dms-text-block"
                                            dangerouslySetInnerHTML={{ __html: document.header?.specialDefinitions }}
                                        />
                                    </TabPane>

                                    <TabPane tabId="6">

                                        <div
                                            className="dms-text-block"
                                            dangerouslySetInnerHTML={{ __html: document.header?.procedure || "" }}
                                        />

                                    </TabPane>

                                    {/* ASSOCIATED DOCS */}
                                    <TabPane tabId="7">
                                        <Table hover bordered responsive className="align-middle">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Number</th>
                                                    <th>Rev</th>
                                                    <th>Doc Title</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {document.associatedDocs?.length ? (
                                                    document.associatedDocs.map((doc) => (
                                                        <tr key={`${doc.docId}-${doc.rev}`}>
                                                            <td>{doc.docId}</td>
                                                            <td>{formatRev(doc.rev)}</td>
                                                            <td>{doc.title}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="3" className="text-muted text-center">
                                                            {/* Go-live parity (2026-09-24): DMS#23 — QSI-imported associated list */}
                                                            {document.qsiHistory?.associatedText
                                                                ? <div className="text-start text-body" style={{ whiteSpace: "pre-wrap" }}>{document.qsiHistory.associatedText}</div>
                                                                : "No associated documents"}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </TabPane>

                                    {/* ATTACHMENTS */}
                                    <TabPane tabId="8">
                                        <Table hover bordered responsive className="align-middle">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>File Name</th>
                                                    <th>Title</th>
                                                    <th>Subject To Approval</th>
                                                    <th>Download</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {document.attachments?.length ? (
                                                    document.attachments.map((a, i) => (
                                                        <tr key={i}>
                                                            <td>{a.fileName}</td>
                                                            <td>
                                                                {a.attachmentTitle}
                                                                {/* Round DMS-UAT-22 — legacy in-grid title edit */}
                                                                <Button color="link" size="sm" className="p-0 ms-2" title="Rename attachment title"
                                                                    onClick={async () => {
                                                                        const next = window.prompt("Attachment title", a.attachmentTitle || "");
                                                                        if (next === null) return;
                                                                        try {
                                                                            await api.put(`/dms/${dmsId}/attachments/title`, { fileName: a.fileName, attachmentTitle: next });
                                                                            toast.success("Attachment title updated.");
                                                                            a.attachmentTitle = next;
                                                                            if (typeof refetchDocument === "function") refetchDocument();
                                                                        } catch (err) {
                                                                            toast.error(err?.response?.data?.error || err?.message || "Rename failed.");
                                                                        }
                                                                    }}>
                                                                    <i className="ri-pencil-line"></i>
                                                                </Button>
                                                            </td>
                                                            <td>{a.subjectToApproval ? "Yes" : "No"}</td>
                                                            <td>
                                                                {/* Owner finding (2026-10-01): a plain link to the api route landed on
                                                                    the dashboard home (the site router owns that path, and a link
                                                                    cannot carry the sign-in). Fetch with the sign-in and save, like
                                                                    the edit form does. */}
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-link p-0"
                                                                    title={`Download ${a.fileName || "attachment"}`}
                                                                    onClick={() => downloadDmsAttachment(a).catch((err) =>
                                                                        toast.error(err?.response?.data?.error || err?.message || "Attachment download failed."))}
                                                                >
                                                                    <FaDownload />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="4" className="text-muted text-center">
                                                            No attachments
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </TabPane>

                                    {/* PROPERTIES — Round DMS-UAT-1: shared renderer with
                                        author display + authorized edit (Issue 15/16) */}
                                    {renderPropertiesTab("9")}

                                    {/* Phase 2.6 — Revisions tab (non-ECN). Customer Approval
                                        is ECN-specific so we don't render it here. */}
                                    {renderRevisionsTab("10")}

                                </>
                            )}
                        </TabContent>
                    </>
                )}
            </ModalBody>
        </Modal>

        {/* ─────────────────────────────────────────────────────────
            Round R4 (2026) — Per-action confirmation modal.

            Opened when the user clicks any button in the manager
            action bar. Shows the legacy-wording confirm message and
            (for /release) the Implementation Date picker. All errors
            surface inline so the parent view stays open and the user
            can retry without losing context.
        ───────────────────────────────────────────────────────────── */}
        <Modal
            isOpen={!!pendingAction}
            toggle={closeConfirm}
            backdrop={actionLoading ? "static" : true}
            keyboard={!actionLoading}
        >
            {pendingAction && (
                <>
                    <ModalHeader toggle={closeConfirm}>{pendingAction.confirmTitle}</ModalHeader>
                    <ModalBody>
                        <p className="mb-3">{pendingAction.confirmMessage}</p>

                        {pendingAction.needsImplDate && (
                            <FormGroup>
                                <Label for="r4_implDate">Implementation Date</Label>
                                <Input
                                    type="date"
                                    id="r4_implDate"
                                    value={implDate}
                                    onChange={(e) => setImplDate(e.target.value)}
                                    disabled={actionLoading}
                                />
                            </FormGroup>
                        )}

                        {/* Round R5 (2026) — deny-comments textarea.
                            Legacy DMSNew.vb:2307 prompt wording:
                              "Please enter any comments you may wish to pass
                               along with your request for revision."
                            Server enforces 5-char min (post-trim) and 255 max.
                            FE matches: button stays disabled until the trimmed
                            value clears 5 chars, and the maxLength attribute
                            prevents the user from typing past 255. */}
                        {pendingAction.key === "deny" && (
                            <FormGroup>
                                <Label for="r5_denyComments">
                                    Please enter any comments you may wish to pass
                                    along with your request for revision.
                                </Label>
                                <Input
                                    type="textarea"
                                    id="r5_denyComments"
                                    rows={4}
                                    maxLength={255}
                                    value={denyComments}
                                    onChange={(e) => setDenyComments(e.target.value)}
                                    placeholder="What specifically needs to change before this can be approved?"
                                    disabled={actionLoading}
                                />
                                <div className="d-flex justify-content-between mt-1">
                                    <small className={`text-${denyComments.trim().length < 5 ? "danger" : "muted"}`}>
                                        {denyComments.trim().length < 5
                                            ? `Minimum 5 characters (${denyComments.trim().length} so far).`
                                            : "Looks good."}
                                    </small>
                                    <small className="text-muted">
                                        {denyComments.length} / 255
                                    </small>
                                </div>
                            </FormGroup>
                        )}

                        {actionError && (
                            <Alert color="danger" className="mb-0 mt-2" style={{ whiteSpace: "pre-wrap" }}>
                                <i className="ri-error-warning-line align-bottom me-1"></i>
                                {actionError}
                            </Alert>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="light" onClick={closeConfirm} disabled={actionLoading}>
                            Cancel
                        </Button>
                        <Button
                            color={pendingAction.tone}
                            onClick={executeAction}
                            disabled={
                                actionLoading
                                || (pendingAction.needsImplDate && !implDate)
                                || (pendingAction.key === "deny" && denyComments.trim().length < 5)
                            }
                        >
                            {actionLoading ? (
                                <><Spinner size="sm" /> Working…</>
                            ) : (
                                pendingAction.confirmLabel
                            )}
                        </Button>
                    </ModalFooter>
                </>
            )}
        </Modal>

        {/* Owner (2026-10-02): print preview of the document or a report. The browser's
            PDF viewer shows the pages; Print and Download are in the header. */}
        <Modal isOpen={!!preview} toggle={closePreview} size="xl" centered className="dms-preview-modal">
            <ModalHeader toggle={closePreview} className="border-bottom bg-light">
                <div className="d-flex align-items-center justify-content-between w-100 gap-3">
                    <span className="fw-semibold">{preview?.title || "Print Preview"}</span>
                    <div className="d-flex gap-2 me-3">
                        <Button color="primary" size="sm" onClick={printFromPreview}>
                            <i className="ri-printer-line align-bottom me-1"></i>Print
                        </Button>
                        <Button color="secondary" size="sm" outline onClick={downloadFromPreview}>
                            <i className="ri-download-2-line align-bottom me-1"></i>Download
                        </Button>
                    </div>
                </div>
            </ModalHeader>
            <ModalBody className="p-0">
                {preview?.url && (
                    <iframe
                        ref={previewFrameRef}
                        src={preview.url}
                        title={preview.title || "Print Preview"}
                        style={{ width: "100%", height: "calc(100vh - 58px)", border: 0, display: "block" }}
                    />
                )}
            </ModalBody>
        </Modal>
        </>
    );
}
